// src/components/dashboard/settings/PracticeSetting.js

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  LinearProgress,
  Chip,
} from "@mui/material";

// Core Guaranteed MUI Icons
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import LocalHospitalRoundedIcon from "@mui/icons-material/LocalHospitalRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RotateLeftRoundedIcon from "@mui/icons-material/RotateLeftRounded";
import FaxRoundedIcon from "@mui/icons-material/PrintRounded";

const usStates = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
  "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
  "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
  "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
  "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
  "New Hampshire", "New Jersey", "New Mexico", "New York",
  "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
  "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
  "West Virginia", "Wisconsin", "Wyoming"
];

const posOptions = [
  "01 - Pharmacy",
  "02 - Telehealth Provided Other than in Patient's Home",
  "07 - Tribal 638 Free-standing Facility",
  "10 - Telehealth Provided in Patient's Home",
  "11 - Office / Clinic",
  "21 - Inpatient Hospital",
  "22 - On Campus-Outpatient Hospital",
  "31 - Skilled Nursing Facility",
];

const emptyForm = {
  // Section 1: Details
  practiceName: "",
  orgBusiness: "",
  taxId: "",
  address: "",
  city: "",
  state: "",
  zipCode: "",
  phoneNo: "",
  fax: "",
  email: "",
  billingEmail: "",
  website: "",

  // Section 2: Billing Provider
  bpFirstName: "",
  bpLastName: "",
  bpLicenseType: "",
  bpState: "",
  bpTaxId: "",
  bpNpi: "",
  bpTaxonomy: "",
  bpAddress: "",
  bpCity: "",
  bpZipCode: "",

  // Section 3: Default Settings
  pos: "",
  serviceLocation: "",
};

const PracticeSetting = () => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      const darkActive =
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme");
      setIsDark(darkActive);
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState(false);

  // Unified Clinical Design System Palette
  const tealMain = "#1E5E5F";
  const tealButton = "#245E65";
  const addGreen = "#057A44";
  const labelTeal = "#276A6C";
  const pastelTealBg = "#D9EBEF";

  const cardBg = isDark ? "#0F172A" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#245E65";
  const softCardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#EAEFF4";
  const primaryTextColor = isDark ? "#F8FAFC" : "#0A2540";
  const secondaryTextColor = isDark ? "#94A3B8" : "#64748B";
  const controlBg = isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF";
  const controlBorder = isDark ? "rgba(255, 255, 255, 0.12)" : "#CBD5E1";
  const sectionBg = isDark ? "rgba(255, 255, 255, 0.02)" : "#FDFDFD";
  const menuBg = isDark ? "#1E293B" : "#FFFFFF";

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleReset = () => {
    setFormData(emptyForm);
  };

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      setSnackbar(true);
    }, 650);
  };

  const completionPercentage = useMemo(() => {
    const fields = Object.values(formData);
    const filled = fields.filter((val) => String(val).trim() !== "").length;
    return Math.round((filled / fields.length) * 100);
  }, [formData]);

  const fieldStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
      backgroundColor: controlBg,
      fontSize: "0.85rem",
      color: primaryTextColor,
      transition: "all 0.2s ease",
      "& fieldset": { borderColor: controlBorder },
      "&:hover fieldset": { borderColor: tealMain },
      "&.Mui-focused fieldset": { borderColor: tealMain },
      "& input::placeholder": {
        color: secondaryTextColor,
        opacity: 0.7,
      },
    },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: menuBg,
        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#B8D6DD"}`,
        color: primaryTextColor,
        borderRadius: "8px",
        maxHeight: "260px",
        boxShadow: isDark ? "0 14px 34px rgba(0,0,0,0.6)" : "0 8px 24px rgba(15,23,42,0.08)",
        "& .MuiMenuItem-root": {
          fontSize: "0.84rem",
          py: 0.8,
          "&:hover": {
            backgroundColor: isDark ? "rgba(255,255,255,0.06)" : pastelTealBg,
          },
          "&.Mui-selected": {
            backgroundColor: isDark ? "rgba(30, 94, 95, 0.35)" : pastelTealBg,
            color: tealButton,
            fontWeight: 700,
          },
        },
      },
    },
  };

  return (
    <Box sx={{ width: "100%", pb: 6 }}>
      {/* 1. TOP HEADER & GAUGE BAR */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.2, md: "20px 26px" },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          gap: 2.5,
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              backgroundColor: tealMain,
              color: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <BusinessRoundedIcon sx={{ fontSize: "1.7rem" }} />
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
              <Typography
                sx={{
                  fontSize: { xs: "1.2rem", md: "1.35rem" },
                  fontWeight: 800,
                  color: primaryTextColor,
                  letterSpacing: "-0.3px",
                  lineHeight: 1.2,
                }}
              >
                Practice Settings
              </Typography>
              <Chip
                label="Master Entity"
                size="small"
                sx={{
                  height: "22px",
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  backgroundColor: pastelTealBg,
                  color: tealButton,
                  borderRadius: "6px",
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: "0.82rem",
                color: secondaryTextColor,
                fontWeight: 500,
                mt: 0.3,
              }}
            >
              Configure practice credentials, tax identifiers, and provider billing demographics
            </Typography>
          </Box>
        </Box>

        {/* Completion Gauge Card */}
        <Box
          sx={{
            minWidth: { xs: "100%", md: "230px" },
            p: 1.5,
            borderRadius: "12px",
            backgroundColor: isDark ? "#162238" : "#F8FAFC",
            border: `1px solid ${softCardBorder}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.6 }}>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: secondaryTextColor, textTransform: "uppercase" }}>
              Form Completeness
            </Typography>
            <Typography sx={{ fontSize: "0.84rem", fontWeight: 800, color: tealButton }}>
              {completionPercentage}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={completionPercentage}
            sx={{
              height: "6px",
              borderRadius: "6px",
              backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "#E2E8F0",
              "& .MuiLinearProgress-bar": {
                borderRadius: "6px",
                backgroundColor: addGreen,
              },
            }}
          />
        </Box>
      </Box>

      {/* 2. FORM CONFIGURATION CARD */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.5, md: "26px 30px" },
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
        }}
      >
        {/* SECTION 1: DETAILS */}
        <Box
          sx={{
            p: { xs: 2, md: 2.8 },
            borderRadius: "14px",
            backgroundColor: sectionBg,
            border: `1px solid ${softCardBorder}`,
            mb: 3.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                backgroundColor: pastelTealBg,
                color: tealButton,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <BusinessRoundedIcon sx={{ fontSize: "1.1rem" }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: primaryTextColor }}>
                Details
              </Typography>
              <Typography sx={{ fontSize: "0.74rem", color: secondaryTextColor }}>
                General practice identity and contact endpoints
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
              columnGap: 2.5,
              rowGap: 2.2,
            }}
          >
            {/* Practice Name */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                Practice Name
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={formData.practiceName}
                onChange={handleChange("practiceName")}
                placeholder="Enter Practice Name"
                sx={fieldStyles}
              />
            </Box>

            {/* Organization Business */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                Organization Business
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={formData.orgBusiness}
                onChange={handleChange("orgBusiness")}
                placeholder="Enter Organization Business"
                sx={fieldStyles}
              />
            </Box>

            {/* Tax ID */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                Tax ID
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={formData.taxId}
                onChange={handleChange("taxId")}
                placeholder="Enter Tax ID"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeRoundedIcon sx={{ fontSize: "1.05rem", color: secondaryTextColor }} />
                    </InputAdornment>
                  ),
                }}
                sx={fieldStyles}
              />
            </Box>

            {/* Address */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                Address
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={formData.address}
                onChange={handleChange("address")}
                placeholder="Enter Street Address"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOnRoundedIcon sx={{ fontSize: "1.05rem", color: secondaryTextColor }} />
                    </InputAdornment>
                  ),
                }}
                sx={fieldStyles}
              />
            </Box>

            {/* City */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                City
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={formData.city}
                onChange={handleChange("city")}
                placeholder="Enter City"
                sx={fieldStyles}
              />
            </Box>

            {/* State */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                State
              </Typography>
              <Select
                fullWidth
                displayEmpty
                size="small"
                value={formData.state}
                onChange={handleChange("state")}
                IconComponent={KeyboardArrowDownRoundedIcon}
                MenuProps={menuProps}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <Typography sx={{ color: secondaryTextColor, fontSize: "0.85rem" }}>
                        Select State...
                      </Typography>
                    );
                  }
                  return selected;
                }}
                sx={{
                  borderRadius: "6px",
                  backgroundColor: controlBg,
                  fontSize: "0.85rem",
                  color: primaryTextColor,
                  "& fieldset": { borderColor: controlBorder },
                  "&:hover fieldset": { borderColor: tealMain },
                  "&.Mui-focused fieldset": { borderColor: tealMain },
                  "& .MuiSelect-icon": { color: secondaryTextColor },
                }}
              >
                {usStates.map((st) => (
                  <MenuItem key={st} value={st}>
                    {st}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Zip Code */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                Zip Code
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={formData.zipCode}
                onChange={handleChange("zipCode")}
                placeholder="Enter Zip Code"
                sx={fieldStyles}
              />
            </Box>

            {/* Phone No */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                Phone No
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={formData.phoneNo}
                onChange={handleChange("phoneNo")}
                placeholder="Enter Phone Number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneRoundedIcon sx={{ fontSize: "1.05rem", color: secondaryTextColor }} />
                    </InputAdornment>
                  ),
                }}
                sx={fieldStyles}
              />
            </Box>

            {/* Fax */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                Fax
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={formData.fax}
                onChange={handleChange("fax")}
                placeholder="Enter Fax Number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaxRoundedIcon sx={{ fontSize: "1.05rem", color: secondaryTextColor }} />
                    </InputAdornment>
                  ),
                }}
                sx={fieldStyles}
              />
            </Box>

            {/* Email */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                Email
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="email"
                value={formData.email}
                onChange={handleChange("email")}
                placeholder="Enter Email Address"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailRoundedIcon sx={{ fontSize: "1.05rem", color: secondaryTextColor }} />
                    </InputAdornment>
                  ),
                }}
                sx={fieldStyles}
              />
            </Box>

            {/* Billing Email Address */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                Billing Email Address
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="email"
                value={formData.billingEmail}
                onChange={handleChange("billingEmail")}
                placeholder="Enter Billing Email Address"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailRoundedIcon sx={{ fontSize: "1.05rem", color: secondaryTextColor }} />
                    </InputAdornment>
                  ),
                }}
                sx={fieldStyles}
              />
            </Box>

            {/* Website */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                Website
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={formData.website}
                onChange={handleChange("website")}
                placeholder="Enter Website URL"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LanguageRoundedIcon sx={{ fontSize: "1.05rem", color: secondaryTextColor }} />
                    </InputAdornment>
                  ),
                }}
                sx={fieldStyles}
              />
            </Box>
          </Box>
        </Box>

        {/* SECTION 2: BILLING PROVIDER */}
        <Box
          sx={{
            p: { xs: 2, md: 2.8 },
            borderRadius: "14px",
            backgroundColor: sectionBg,
            border: `1px solid ${softCardBorder}`,
            mb: 3.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                backgroundColor: pastelTealBg,
                color: tealButton,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LocalHospitalRoundedIcon sx={{ fontSize: "1.1rem" }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: primaryTextColor }}>
                Billing Provider
              </Typography>
              <Typography sx={{ fontSize: "0.74rem", color: secondaryTextColor }}>
                Medical NPI registration and primary practitioner credentials
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
              columnGap: 2.5,
              rowGap: 2.2,
            }}
          >
            {/* First Name */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                First Name
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter First Name"
                value={formData.bpFirstName}
                onChange={handleChange("bpFirstName")}
                sx={fieldStyles}
              />
            </Box>

            {/* Last Name */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                Last Name
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter Last Name"
                value={formData.bpLastName}
                onChange={handleChange("bpLastName")}
                sx={fieldStyles}
              />
            </Box>

            {/* Type Of License */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                Type Of License
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter license type (e.g., MD, DO)"
                value={formData.bpLicenseType}
                onChange={handleChange("bpLicenseType")}
                sx={fieldStyles}
              />
            </Box>

            {/* State */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                State
              </Typography>
              <Select
                fullWidth
                displayEmpty
                size="small"
                value={formData.bpState}
                onChange={handleChange("bpState")}
                IconComponent={KeyboardArrowDownRoundedIcon}
                MenuProps={menuProps}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <Typography sx={{ color: secondaryTextColor, fontSize: "0.85rem" }}>
                        Select State...
                      </Typography>
                    );
                  }
                  return selected;
                }}
                sx={{
                  borderRadius: "6px",
                  backgroundColor: controlBg,
                  fontSize: "0.85rem",
                  color: primaryTextColor,
                  "& fieldset": { borderColor: controlBorder },
                  "&:hover fieldset": { borderColor: tealMain },
                  "&.Mui-focused fieldset": { borderColor: tealMain },
                  "& .MuiSelect-icon": { color: secondaryTextColor },
                }}
              >
                {usStates.map((st) => (
                  <MenuItem key={st} value={st}>
                    {st}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Tax Id */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                Tax Id
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter Tax id"
                value={formData.bpTaxId}
                onChange={handleChange("bpTaxId")}
                sx={fieldStyles}
              />
            </Box>

            {/* NPI */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                NPI (National Provider Identifier)
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter 10-digit NPI"
                value={formData.bpNpi}
                onChange={handleChange("bpNpi")}
                sx={fieldStyles}
              />
            </Box>

            {/* Taxonomy */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                Taxonomy
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter Taxonomy Code"
                value={formData.bpTaxonomy}
                onChange={handleChange("bpTaxonomy")}
                sx={fieldStyles}
              />
            </Box>

            {/* Address */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                Address
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter Provider Address"
                value={formData.bpAddress}
                onChange={handleChange("bpAddress")}
                sx={fieldStyles}
              />
            </Box>

            {/* City */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                City
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter City"
                value={formData.bpCity}
                onChange={handleChange("bpCity")}
                sx={fieldStyles}
              />
            </Box>

            {/* Zip Code */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                Zip Code
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter ZipCode"
                value={formData.bpZipCode}
                onChange={handleChange("bpZipCode")}
                sx={fieldStyles}
              />
            </Box>
          </Box>
        </Box>

        {/* SECTION 3: DEFAULT SETTINGS */}
        <Box
          sx={{
            p: { xs: 2, md: 2.8 },
            borderRadius: "14px",
            backgroundColor: sectionBg,
            border: `1px solid ${softCardBorder}`,
            mb: 3.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                backgroundColor: pastelTealBg,
                color: tealButton,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <TuneRoundedIcon sx={{ fontSize: "1.1rem" }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: primaryTextColor }}>
                Default Settings
              </Typography>
              <Typography sx={{ fontSize: "0.74rem", color: secondaryTextColor }}>
                Encounter defaults, place of service mappings, and location presets
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
              columnGap: 2.5,
              rowGap: 2.2,
            }}
          >
            {/* Place of Service */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                Place of Service (POS)
              </Typography>
              <Select
                fullWidth
                displayEmpty
                size="small"
                value={formData.pos}
                onChange={handleChange("pos")}
                IconComponent={KeyboardArrowDownRoundedIcon}
                MenuProps={menuProps}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <Typography sx={{ color: secondaryTextColor, fontSize: "0.85rem" }}>
                        Select Place of Service...
                      </Typography>
                    );
                  }
                  return selected;
                }}
                sx={{
                  borderRadius: "6px",
                  backgroundColor: controlBg,
                  fontSize: "0.85rem",
                  color: primaryTextColor,
                  "& fieldset": { borderColor: controlBorder },
                  "&:hover fieldset": { borderColor: tealMain },
                  "&.Mui-focused fieldset": { borderColor: tealMain },
                  "& .MuiSelect-icon": { color: secondaryTextColor },
                }}
              >
                {posOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Service Location */}
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal, mb: 0.6 }}>
                Service Location
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter Service Location"
                value={formData.serviceLocation}
                onChange={handleChange("serviceLocation")}
                sx={fieldStyles}
              />
            </Box>
          </Box>
        </Box>

        {/* BOTTOM DUAL ACTIONS */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            pt: 2.5,
            borderTop: `1px solid ${softCardBorder}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircleRoundedIcon sx={{ fontSize: "1.1rem", color: "#15803D" }} />
            <Typography sx={{ fontSize: "0.78rem", color: secondaryTextColor, fontWeight: 500 }}>
              Auto-validation enabled for NPI & Tax ID.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={handleReset}
              startIcon={<RotateLeftRoundedIcon sx={{ fontSize: "1.1rem !important" }} />}
              sx={{
                height: "40px",
                borderColor: tealButton,
                color: tealButton,
                fontWeight: 700,
                fontSize: "0.82rem",
                borderRadius: "6px",
                px: 2.2,
                textTransform: "none",
                "&:hover": {
                  borderColor: tealMain,
                  backgroundColor: "rgba(36, 94, 101, 0.04)",
                },
              }}
            >
              Clear
            </Button>

            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              startIcon={
                saving ? (
                  <CircularProgress size={16} sx={{ color: "#FFFFFF" }} />
                ) : (
                  <SaveRoundedIcon sx={{ fontSize: "1.15rem !important" }} />
                )
              }
              sx={{
                height: "40px",
                backgroundColor: addGreen,
                color: "#FFFFFF",
                fontWeight: 700,
                fontSize: "0.82rem",
                borderRadius: "6px",
                px: 2.8,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#046337",
                  boxShadow: "none",
                },
              }}
            >
              {saving ? "Saving Changes..." : "Save Settings"}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Toast Feedback */}
      <Snackbar
        open={snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar(false)}
          severity="success"
          sx={{
            width: "100%",
            borderRadius: "8px",
            fontWeight: 700,
            fontSize: "0.84rem",
            backgroundColor: isDark ? "#064E3B" : "#DCFCE7",
            color: isDark ? "#6EE7B7" : "#15803D",
            border: `1px solid ${isDark ? "rgba(110, 231, 183, 0.2)" : "#BBF7D0"}`,
          }}
        >
          Practice Settings & Provider details updated successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PracticeSetting;